import { LocalStorageUtils } from "./local-storage";

const KEYS = {
  CHECKOUT_ID: "checkout_id",
  CART_TOKEN: "cart_token",
  SHOPIFY_CART_ITEMS: "shopify_cart_items",
};

// Language-specific keys
const getLanguageSpecificKey = (baseKey: string, language: string) => {
  return `${baseKey}_${language}`;
};

export const CartStorage = {
  saveCheckoutId: (checkoutId: string): void => {
    if (checkoutId) {
      LocalStorageUtils.setItem(KEYS.CHECKOUT_ID, checkoutId);
    }
  },
  getCheckoutId: (): string | null => {
    return LocalStorageUtils.getItem(KEYS.CHECKOUT_ID, null);
  },

  saveCartToken: (token: string): void => {
    if (token) {
      LocalStorageUtils.setItem(KEYS.CART_TOKEN, token);
    }
  },

  getCartToken: (): string | null => {
    return LocalStorageUtils.getItem(KEYS.CART_TOKEN, null);
  },

  saveShopifyCartItems: (items: any[]): void => {
    LocalStorageUtils.setItem(KEYS.SHOPIFY_CART_ITEMS, items);
  },

  getShopifyCartItems: (): any[] => {
    return LocalStorageUtils.getItem(KEYS.SHOPIFY_CART_ITEMS, []);
  },

  clear: (): void => {
    LocalStorageUtils.removeItem(KEYS.CHECKOUT_ID);
    LocalStorageUtils.removeItem(KEYS.CART_TOKEN);
    LocalStorageUtils.removeItem(KEYS.SHOPIFY_CART_ITEMS);

    for (const key of Object.values(KEYS)) {
      LocalStorageUtils.removeItem(key);
    }

    console.log("Cart storage cleared, including checkout ID");
  },

  // Language-specific cart methods
  saveCartForLanguage: (
    language: string,
    cartData: {
      checkoutId?: string;
      cartToken?: string;
      items?: any[];
    },
  ): void => {
    if (cartData.checkoutId) {
      LocalStorageUtils.setItem(
        getLanguageSpecificKey(KEYS.CHECKOUT_ID, language),
        cartData.checkoutId,
      );
    }
    if (cartData.cartToken) {
      LocalStorageUtils.setItem(
        getLanguageSpecificKey(KEYS.CART_TOKEN, language),
        cartData.cartToken,
      );
    }
    if (cartData.items) {
      LocalStorageUtils.setItem(
        getLanguageSpecificKey(KEYS.SHOPIFY_CART_ITEMS, language),
        cartData.items,
      );
    }
  },

  getCartForLanguage: (
    language: string,
  ): {
    checkoutId: string | null;
    cartToken: string | null;
    items: any[];
  } => {
    const checkoutId = LocalStorageUtils.getItem(
      getLanguageSpecificKey(KEYS.CHECKOUT_ID, language),
      null,
    );
    const cartToken = LocalStorageUtils.getItem(
      getLanguageSpecificKey(KEYS.CART_TOKEN, language),
      null,
    );
    const items = LocalStorageUtils.getItem(
      getLanguageSpecificKey(KEYS.SHOPIFY_CART_ITEMS, language),
      [],
    );

    return { checkoutId, cartToken, items };
  },

  switchToLanguageCart: (fromLanguage: string, toLanguage: string): void => {
    // Save current cart state for the current language
    const currentCheckoutId = CartStorage.getCheckoutId();
    const currentCartToken = CartStorage.getCartToken();
    const currentItems = CartStorage.getShopifyCartItems();

    if (currentCheckoutId || currentCartToken || currentItems.length > 0) {
      CartStorage.saveCartForLanguage(fromLanguage, {
        checkoutId: currentCheckoutId || undefined,
        cartToken: currentCartToken || undefined,
        items: currentItems,
      });
    }

    // Clear current cart storage
    CartStorage.clear();

    // Load cart for the target language
    const targetCart = CartStorage.getCartForLanguage(toLanguage);

    if (targetCart.checkoutId) {
      CartStorage.saveCheckoutId(targetCart.checkoutId);
    }
    if (targetCart.cartToken) {
      CartStorage.saveCartToken(targetCart.cartToken);
    }
    if (targetCart.items.length > 0) {
      CartStorage.saveShopifyCartItems(targetCart.items);
    }
  },
};
